import React, { useMemo, useState } from "react";
import { useHSE } from "../lib/store";
import { KPI_ROWS, MONTHS } from "../lib/sampleData";
import { Search, Save, RotateCcw, Download, FileSpreadsheet } from "lucide-react";
import { Button } from "../components/ui/button";
import { exportCSV, exportXLSX } from "../lib/exportUtils";
import { toast } from "sonner";

export default function MonthlyKPI() {
  const { data, setData, filters, setFilters, resetSample, siteNames } = useHSE();
  const [search, setSearch] = useState("");
  const [site, setSite] = useState(siteNames[0]);
  const [draft, setDraft] = useState(data.monthlyKPI);

  const visibleRows = useMemo(() => {
    return KPI_ROWS.filter((r) => r.name.toLowerCase().includes(search.toLowerCase()));
  }, [search]);

  const getVal = (row, idx) => {
    if (row.computed) {
      const lti = draft[site].lti[idx];
      const wd = draft[site].workdaysLost[idx];
      const mh = draft[site].manhours[idx];
      if (row.key === "ltifRate") return mh ? +((lti * 1_000_000) / mh).toFixed(2) : 0;
      if (row.key === "ltisr") return mh ? +((wd * 1_000_000) / mh).toFixed(2) : 0;
    }
    return draft[site]?.[row.key]?.[idx] ?? 0;
  };

  const ytd = (row) => {
    let s = 0;
    for (let i = 0; i < 12; i++) s += Number(getVal(row, i)) || 0;
    return row.key === "ltifRate" || row.key === "ltisr" ? +(s / 12).toFixed(2) : s;
  };

  const consolidate = (row) => {
    let s = 0;
    siteNames.forEach((st) => {
      for (let i = 0; i < 12; i++) {
        if (row.computed) {
          const lti = draft[st].lti[i];
          const wd = draft[st].workdaysLost[i];
          const mh = draft[st].manhours[i];
          if (row.key === "ltifRate") s += mh ? (lti * 1_000_000) / mh : 0;
          if (row.key === "ltisr") s += mh ? (wd * 1_000_000) / mh : 0;
        } else {
          s += Number(draft[st]?.[row.key]?.[i] || 0);
        }
      }
    });
    return row.computed ? +(s / (12 * siteNames.length)).toFixed(2) : Math.round(s * 100) / 100;
  };

  const update = (rowKey, idx, val) => {
    if (KPI_ROWS.find(r => r.key === rowKey)?.computed) return;
    const num = val === "" ? 0 : Number(val);
    setDraft((d) => ({
      ...d,
      [site]: {
        ...d[site],
        [rowKey]: d[site][rowKey].map((v, i) => (i === idx ? num : v)),
      },
    }));
  };

  const saveAll = () => {
    setData({ ...data, monthlyKPI: draft });
    toast.success("Monthly KPI saved");
  };

  const reset = () => {
    if (!confirm("Reset all data to sample? This will erase your edits.")) return;
    resetSample();
    setTimeout(() => window.location.reload(), 100);
  };

  const exportRows = () => visibleRows.map((row) => {
    const o = { KPI: row.name };
    MONTHS.forEach((m, i) => o[m] = getVal(row, i));
    o.YTD = ytd(row); o.Consolidate = consolidate(row);
    return o;
  });

  const cellColor = (row, val) => {
    const num = Number(val) || 0;
    const danger = ["lti", "incidents", "ltifRate", "ltisr", "ncrOpen", "sorOpen", "fines", "violations", "workdaysLost"];
    const positive = ["training", "inducted", "tbt", "obsClosed", "drills", "awards", "wasteRec", "inspections"];
    if (danger.includes(row.key) && num > 0) return num > 3 ? "#FEE2E2" : "#FEF3C7";
    if (positive.includes(row.key) && num > 0) return "#DCFCE7";
    return "transparent";
  };

  return (
    <div className="space-y-4" data-testid="monthly-kpi-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl tracking-tight text-[#0E2336]">Monthly KPI Data</h1>
          <div className="text-sm text-slate-500">Editable matrix · Site: <b>{site}</b> · Year {filters.year}</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="kpi-site-select" value={site} onChange={(e) => setSite(e.target.value)}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white max-w-[160px]">
            {siteNames.map((s) => <option key={s}>{s}</option>)}
          </select>
          <select data-testid="kpi-year-select" value={filters.year} onChange={(e) => setFilters({ ...filters, year: e.target.value })}
            className="border border-slate-200 rounded-md px-2.5 py-1.5 text-sm bg-white">
            {["2023", "2024", "2025", "2026"].map((y) => <option key={y}>{y}</option>)}
          </select>
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-md px-2.5 py-1.5">
            <Search className="w-4 h-4 text-slate-400" />
            <input data-testid="kpi-search" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search KPI…" className="text-xs bg-transparent focus:outline-none w-40" />
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
            onClick={() => exportCSV(exportRows(), "monthly-kpi.csv")}>
            <Download className="w-3.5 h-3.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5"
            onClick={() => exportXLSX(exportRows(), "monthly-kpi.xlsx", "Monthly KPI")}>
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs gap-1.5" onClick={reset}>
            <RotateCcw className="w-3.5 h-3.5" /> Reset Sample
          </Button>
          <Button data-testid="kpi-save-btn" onClick={saveAll} size="sm"
            className="h-8 text-xs gap-1.5 font-semibold" style={{ background: "#54A57B", color: "white" }}>
            <Save className="w-3.5 h-3.5" /> Save Changes
          </Button>
        </div>
      </div>

      <div className="command-card overflow-hidden">
        <div className="overflow-auto" style={{ maxHeight: "calc(100vh - 240px)" }}>
          <table className="excel-table w-full text-[12px]">
            <thead>
              <tr>
                <th className="sticky-col text-left py-2 px-3 text-[10px] uppercase tracking-wider font-semibold min-w-[280px]">KPI Name</th>
                {MONTHS.map((m) => (
                  <th key={m} className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[64px]">{m}</th>
                ))}
                <th className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[80px]" style={{ background: "#FFB517", color: "#183C55" }}>YTD</th>
                <th className="text-center py-2 px-2 text-[10px] uppercase tracking-wider font-semibold min-w-[100px]" style={{ background: "#54A57B", color: "white" }}>Consolidate</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((row, ri) => (
                <tr key={row.key} className={ri % 2 ? "bg-slate-50/60" : ""}>
                  <td className="sticky-col py-1.5 px-3 font-medium text-[#0E2336] border-r border-slate-100 whitespace-nowrap">
                    {row.name}
                    {row.computed && <span className="ml-2 text-[9px] uppercase tracking-wider text-[#54A57B] font-bold">auto</span>}
                  </td>
                  {MONTHS.map((m, mi) => {
                    const v = getVal(row, mi);
                    const bg = cellColor(row, v);
                    return (
                      <td key={mi} style={{ background: bg }} className="border border-slate-100">
                        <input
                          data-testid={`kpi-cell-${row.key}-${mi}`}
                          value={v}
                          readOnly={row.computed}
                          onChange={(e) => update(row.key, mi, e.target.value)}
                          className={row.computed ? "text-slate-400" : ""}
                        />
                      </td>
                    );
                  })}
                  <td className="text-right py-1.5 px-3 font-bold tabular text-[#7A5300] border-l border-slate-200" style={{ background: "#FFF8E6" }}>
                    {ytd(row).toLocaleString()}
                  </td>
                  <td className="text-right py-1.5 px-3 font-bold tabular text-[#1F5F3D]" style={{ background: "#ECFAF2" }}>
                    {consolidate(row).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Formula label="LTIF Rate" formula="LTI × 1,000,000 / Manhours" />
        <Formula label="LTISR" formula="Workdays Lost × 1,000,000 / Manhours" />
        <Formula label="Obs. Closure %" formula="Closed / Raised × 100" />
        <Formula label="Recycled %" formula="Recycled T / Generated T × 100" />
      </div>
    </div>
  );
}

function Formula({ label, formula }) {
  return (
    <div className="command-card p-3.5">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div className="font-mono text-[12px] text-[#183C55] mt-1">{formula}</div>
    </div>
  );
}
